import TableOnly from "components/DataTable/TableOnly";
import PaginationWithState from "components/Pagination/PaginationWithState";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import { truncateString } from "utils/UtilityFuncs";

function CustomerContactSelect({
  isPrimaryId = false,
  isMultiSelect = false,
  customerId,
  handleSelectContact,
}) {
  const dispatch = useDispatch();

  /*   ALL STATES
   ********************************************* */
  const [filterBy, setFilterBy] = useState({
    name: "Filter by All",
    value: null,
  });

  const [customerContactData, setCustomerContactData] = useState(null);

  const [loading, setLoading] = useState(false);

  // Pagination/Search states
  const [pageLimit, setPageLimit] = useState(5);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const [selectedContactList, setSelectedContactList] = useState([]);

  /*   All Function
   ********************************************* */
  // Get Customer Contacts
  const getCustomerContacts = useCallback(
    async ({ clearSearch = false, query, page } = {}) => {
      try {
        setLoading(true);

        const usedQuery = clearSearch
          ? ""
          : typeof query !== "undefined"
            ? query
            : "";
        const usedPage = page ?? pageNumber;
        const { data } = await API.post(
          "/customer-contacts/list/" +
            customerId +
            "?page=" +
            usedPage +
            "&limit=" +
            pageLimit,
          {
            query: usedQuery,
            filterBy:
              usedQuery && usedQuery !== "" ? filterBy.value : undefined,
            isPrimaryId: isPrimaryId,
          },
          { headers: authHeader() },
        );
        if (data?.success) {
          // API returns { success, data, total, page, limit, totalPages }

          let defaultContact = data?.defaultContact;
          let combinedContacts = data?.data || [];
          // combine contacts + default contact, avoiding duplicates
          if (defaultContact) {
            combinedContacts = [defaultContact, ...data?.data];
          }

          setCustomerContactData(combinedContacts || []);
          setTotalPages(data?.totalPages || 0);
        } else {
          dispatch(
            setNotification({
              message: data?.message || "Failed to fetch",
              type: "error",
            }),
          );
        }
      } catch (err) {
        dispatch(setNotification({ message: err.message, type: "error" }));
      } finally {
        setLoading(false);
      }
    },
    [pageNumber, pageLimit, filterBy.value, customerId],
  );

  const copyContent = (val) => {
    navigator.clipboard.writeText(val);
    dispatch(
      setNotification({
        message: "Email Copied",
        type: "success",
      }),
    );
  };

  const handleMultiSelect = (email) => {
    const newSet = new Set(selectedContactList);
    if (newSet.has(email)) {
      newSet.delete(email);
    } else {
      newSet.add(email);
    }
    setSelectedContactList(Array.from(newSet));
  };

  /*   Table Functions
   ********************************************* */

  let filters = [
    { name: "First Name", value: "firstName" },
    { name: "Last Name", value: "lastName" },
    { name: "Department", value: "department" },
    { name: "Phone", value: "phone" },
    // { name: "Mobile Phone", value: "mobile" },
    { name: "Email", value: "email" },
  ];

  const buttons = () => (
    <div className=" d-flex justify-content-end ">
      {/* FilterBy */}
      <button
        type="button"
        className="px-1 bg-primary btn text-dark bg-opacity-10 border border-primary rounded-end mx-2 rounded-start"
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
      >
        {filterBy ? filterBy.name : "Filter by All"}{" "}
        <i className="mdi mdi-chevron-down"></i>
      </button>
      <div className="dropdown-menu">
        <button
          onClick={() => setFilterBy({ name: "Filter by All", value: null })}
          className="dropdown-item"
        >
          Filter by All
        </button>
        {filters.map((item, index) => (
          <button
            key={index}
            onClick={() => setFilterBy(item)}
            className="dropdown-item"
          >
            {item.name}
          </button>
        ))}
      </div>
    </div>
  );

  const table_head = [
    { name: "First Name", value: "firstName" },
    { name: "Last Name", value: "lastName" },
    { name: "Department", value: "department" },
    { name: "Phone", value: "phone" },
    // { name: "Mobile Phone", value: "mobile" },
    { name: "Email", value: "email" },
  ];

  if (isMultiSelect) {
    table_head.unshift({ name: "", value: "select" });
  } else {
    table_head.push({ name: "Actions", value: "actions", align: "end" });
  }

  const table_body = (item, index) => (
    <tr className="" key={item?.id}>
      {isMultiSelect && (
        <td className="py-0 pe-0 align-middle">
          <input
            type="checkbox"
            checked={selectedContactList?.includes(item?.email)}
            onChange={(e) => {
              handleMultiSelect(item.email);
            }}
          />
        </td>
      )}
      <td className="py-0 pe-0 align-middle">
        {truncateString(item?.firstName, 15)}
      </td>
      <td className="py-0 pe-0 align-middle">
        {truncateString(item?.lastName, 15)}
      </td>
      <td className="py-0 pe-0 align-middle">
        {truncateString(item?.department, 15)}{" "}
      </td>
      <td className="py-0 pe-0 align-middle">
        {truncateString(item?.phone, 15)}{" "}
      </td>
      {/* <td className="py-0 pe-0 align-middle">{item?.mobile} </td> */}
      <td
        onDoubleClick={() => {
          copyContent(item?.email);
        }}
        className="py-0 pe-0 align-middle user-select-none"
      >
        {item?.email}{" "}
      </td>
      {!isMultiSelect && (
        <td className="table-action py-0 text-end align-middle">
          <button
            onClick={(e) => {
              handleSelectContact(item);
            }}
            className="btn btn-sm my-1 btn-success"
          >
            Select
          </button>
        </td>
      )}
    </tr>
  );

  useEffect(() => {
    let timeout = setTimeout(() => {
      getCustomerContacts();
    }, 50);
    return () => clearTimeout(timeout);
  }, [getCustomerContacts]);

  return (
    <div className="shadow-lg bg-white px-3 py-2 custom-border-radius">
      <h3>Customer Contacts List</h3>
      <hr className="mb-3 mt-1" />
      {loading && <Spinner />}
      <div className="d-flex justify-content-between mb-1 align-items-center">
        <div>
          <div className="">
            Show &nbsp;
            <div className="btn-group">
              <button
                className="px-1 bg-primary bg-opacity-10 border border-primary rounded-end rounded-start "
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {pageLimit} <i className="mdi mdi-chevron-down"></i>{" "}
              </button>
              <ul className="dropdown-menu w-25">
                <li onClick={() => setPageLimit(5)}>
                  <button className="dropdown-item">5</button>
                </li>
                <li onClick={() => setPageLimit(10)}>
                  <button className="dropdown-item">10</button>
                </li>
                <li onClick={() => setPageLimit(15)}>
                  <button className="dropdown-item">15</button>
                </li>
                <li onClick={() => setPageLimit(20)}>
                  <button className="dropdown-item">20</button>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div className="d-flex align-items-center gap-0">
          {buttons()}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPageNumber(1);
              if (searchTerm && searchTerm !== "") {
                getCustomerContacts({ query: searchTerm, page: 1 });
              } else {
                getCustomerContacts({ clearSearch: true, page: 1 });
              }
            }}
            className="d-flex justify-content-end gap-1 align-items-center"
          >
            <input
              type="search"
              placeholder="Search by name..."
              value={searchTerm}
              className="form-control"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="btn btn-info" type="submit">
              Search
            </button>
            {searchTerm !== "" && (
              <button
                className="btn btn-outline-danger"
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  getCustomerContacts({ clearSearch: true, page: 1 });
                }}
              >
                Clear
              </button>
            )}
          </form>
        </div>
      </div>
      <div className="" style={{ minHeight: "10vh", borderRadius: 15 }}>
        <div className="horizontal-scrollable d-flex flex-grow-1">
          <TableOnly
            table_head={table_head}
            table_body={table_body}
            table_data={customerContactData}
            disableSorting={true}
            actionsOnEnd={true}
          />
        </div>
      </div>

      <div className="d-flex justify-content-end mt-2">
        <PaginationWithState
          totalPages={totalPages}
          currPage={pageNumber}
          setCurrPage={setPageNumber}
        />
      </div>
      {isMultiSelect && (
        <>
          <hr />
          <div className="text-end">
            <button
              className="btn btn-success"
              onClick={() => {
                handleSelectContact(selectedContactList);
              }}
            >
              Confirm Selection
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default CustomerContactSelect;
